import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import TiptapEditor from './TiptapEditor';
import { EmailTemplate, Recipient, AlertInfo } from '../types';
import { extractVariables, generatePersonalizedEmail } from '../utils/templateUtils';
import { templateApi, getDefaultAccountId } from '../services/api';

interface TemplateBuilderProps {
  onTemplateSelect: (template: EmailTemplate) => void;
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

const TemplateBuilder: React.FC<TemplateBuilderProps> = ({ onTemplateSelect, showAlert }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<EmailTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRecipient, setPreviewRecipient] = useState<Recipient>({
    email: 'john.doe@example.com',
    variables: {}
  });

  // Form fields
  const [templateName, setTemplateName] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');


  const loadTemplates = async () => {
    try {
      const accountId = getDefaultAccountId();
      const response = await templateApi.getByAccount(accountId);
      const templatesData = response.templates || [];
      setTemplates(templatesData.map((t: any) => ({
        ...t,
        createdAt: new Date(t.createdAt),
        updatedAt: new Date(t.updatedAt)
      })));
    } catch (error) {
      console.error('Error loading templates:', error);
      showAlert('Failed to load templates. Please check your connection.', 'error');
    }
  };

    // Load templates from API on component mount
  useEffect(() => {
    loadTemplates();
  }, []);
  // Extract variables when content changes
  const [detectedVariables, setDetectedVariables] = useState<string[]>([]);
  useEffect(() => {
    const subjectVars = extractVariables(subject);
    const contentVars = extractVariables(content);
    const allVars = Array.from(new Set([...subjectVars, ...contentVars]));
    setDetectedVariables(allVars);
    
    // Update preview recipient variables
    const newVariables: Record<string, string> = {};
    allVars.forEach(variable => {
      newVariables[variable] = previewRecipient.variables[variable] || `[${variable}]`;
    });
    setPreviewRecipient(prev => ({ ...prev, variables: newVariables }));
  }, [subject, content]);

  const handleNewTemplate = () => {
    setCurrentTemplate(null);
    setTemplateName('');
    setSubject('');
    setContent('');
    setIsEditing(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setCurrentTemplate(template);
    setTemplateName(template.name);
    setSubject(template.subject);
    setContent(template.content);
    setIsEditing(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      showAlert('Please enter a template name', 'error');
      return;
    }
    if (!subject.trim()) {
      showAlert('Please enter a subject', 'error');
      return;
    }
    if (!content.trim()) {
      showAlert('Please enter content', 'error');
      return;
    }

    try {
      const accountId = getDefaultAccountId();
      const templateData = {
        name: templateName.trim(),
        subject: subject.trim(),
        content: content.trim(),
        variables: detectedVariables,
        accountId
      };

      if (currentTemplate) {
        // Update existing template
        await templateApi.update(currentTemplate.id, templateData);
        showAlert('Template updated successfully', 'success');
      } else {
        // Create new template
        await templateApi.create(templateData);
        showAlert('Template created successfully', 'success');
      }

      setIsEditing(false);
      setCurrentTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      showAlert('Failed to save template. Please try again.', 'error');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await templateApi.delete(templateId);
      showAlert('Template deleted successfully', 'success');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showAlert('Failed to delete template. Please try again.', 'error');
    }
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handleUseTemplate = (template: EmailTemplate) => {
    onTemplateSelect(template);
    showAlert('Template selected for compose', 'success');
  };

  const handlePreviewVariableChange = (variable: string, value: string) => {
    setPreviewRecipient(prev => ({
      ...prev,
      variables: { ...prev.variables, [variable]: value }
    }));
  };

  const getPreviewContent = () => {
    if (!subject || !content) return { subject: '', content: '' };
    
    const template: EmailTemplate = {
      id: 'preview',
      name: 'Preview',
      subject,
      content,
      variables: detectedVariables,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return generatePersonalizedEmail(template, previewRecipient);
  };



  if (isEditing) {
    return (
      <Box sx={{ maxWidth: 1000, margin: 'auto', padding: 2 }}>
        <Typography variant="h5" gutterBottom>
          {currentTemplate ? 'Edit Template' : 'Create New Template'}
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                margin="normal"
                helperText="Use {{variableName}} for personalization"
              />
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Email Content
                </Typography>
                <TiptapEditor
                  content={content}
                  onChange={setContent}
                  placeholder="Enter your email content here. Use {{variableName}} for personalization."
                  minHeight={300}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detected Variables
              </Typography>
              {detectedVariables.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {detectedVariables.map(variable => (
                    <Chip key={variable} label={variable} size="small" />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {`No variables detected. Use {{variableName}} syntax.`}
                </Typography>
              )}
              
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveTemplate}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Save Template
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreview}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Preview
                </Button>
                <Button
                  variant="text"
                  onClick={() => setIsEditing(false)}
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Template Preview</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Variable Values (for preview)
                </Typography>
                {detectedVariables.map(variable => (
                  <TextField
                    key={variable}
                    fullWidth
                    label={variable}
                    value={previewRecipient.variables[variable] || ''}
                    onChange={(e) => handlePreviewVariableChange(variable, e.target.value)}
                    margin="dense"
                    size="small"
                  />
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview Result
                </Typography>
                <Box sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary">
                    Subject: {getPreviewContent().subject}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <div dangerouslySetInnerHTML={{ __html: getPreviewContent().content }} />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        mb: 3
      }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Email Templates
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create and manage reusable email templates for your campaigns
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewTemplate}
            sx={{ borderRadius: 3, px: 3 }}
          >
            New Template
          </Button>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>

        {templates.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No templates yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first email template to get started with personalized campaigns
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewTemplate} sx={{ borderRadius: 2 }}>
              Create First Template
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} lg={4} key={template.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.subject}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {template.variables.slice(0, 3).map((variable) => (
                        <Chip key={variable} label={variable} size="small" variant="outlined" />
                      ))}
                      {template.variables.length > 3 && (
                        <Chip label={`+${template.variables.length - 3}`} size="small" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Updated: {template.updatedAt.toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleUseTemplate(template)}
                      sx={{ borderRadius: 2 }}
                    >
                      Use Template
                    </Button>
                    <IconButton onClick={() => handleEditTemplate(template)} size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteTemplate(template.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default TemplateBuilder;
